import React, { useState, useEffect } from 'react';
import {
  FiPlus,
  FiRefreshCw,
  FiUser,
  FiZap,
  FiEdit3,
  FiMessageSquare,
  FiTrash2,
  FiFileText,
} from 'react-icons/fi';
import './TaskHistory.css';
import {
  getTaskHistory,
  getCompleteTaskHistory,
} from '../../services/taskHistory';

const TaskHistory = ({ taskId, isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasOlderChunks, setHasOlderChunks] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);

  useEffect(() => {
    if (isOpen && taskId) {
      // Reset state when modal opens
      setShowAllHistory(false);
      fetchTaskHistory();
    }
  }, [isOpen, taskId]);

  const fetchTaskHistory = async (reset = true) => {
    if (reset) {
      setLoading(true);
      setError(null);
      setHistory([]);
      setNextOffset(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await getTaskHistory(taskId, 10, reset ? 0 : nextOffset);

      if (reset) {
        setHistory(response.history || []);
      } else {
        setHistory((prev) => [...prev, ...(response.history || [])]);
      }

      setHasMore(response.hasMore || false);
      setNextOffset(response.nextOffset || 0);
      setTotalCount(response.totalCount || 0);
      setHasOlderChunks(response.hasOlderChunks || false);
    } catch (err) {
      setError('Failed to load task history');
      console.error('Error fetching task history:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreHistory = () => {
    if (!hasMore || loadingMore) return;
    fetchTaskHistory(false);
  };

  const loadCompleteHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getCompleteTaskHistory(taskId);
      setHistory(response.history || []);
      setHasMore(false);
      setNextOffset(0);
      setTotalCount(response.totalCount || 0);
      setHasOlderChunks(false);
      setShowAllHistory(true);
    } catch (err) {
      setError('Failed to load complete history');
      console.error('Error fetching complete history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getChangeIcon = (changeType) => {
    const icons = {
      CREATE: <FiPlus className="change-icon" />,
      STATUS_CHANGE: <FiRefreshCw className="change-icon" />,
      ASSIGNEE_CHANGE: <FiUser className="change-icon" />,
      PRIORITY_CHANGE: <FiZap className="change-icon" />,
      UPDATE: <FiEdit3 className="change-icon" />,
      COMMENT_ADDED: <FiMessageSquare className="change-icon" />,
      COMMENT_EDITED: <FiEdit3 className="change-icon" />,
      COMMENT_DELETED: <FiTrash2 className="change-icon" />,
    };
    return icons[changeType] || <FiFileText className="change-icon" />;
  };

  const getChangeColor = (changeType) => {
    const colors = {
      CREATE: '#00875A',
      STATUS_CHANGE: '#0052CC',
      ASSIGNEE_CHANGE: '#6554C0',
      PRIORITY_CHANGE: '#FF5630',
      UPDATE: '#172B4D',
      COMMENT_ADDED: '#00875A',
      COMMENT_EDITED: '#FF8B00',
      COMMENT_DELETED: '#DE350B',
    };
    return colors[changeType] || '#172B4D';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatChangeValue = (value) => {
    if (value === null || value === undefined) return 'None';
    if (typeof value === 'string' && value.trim() === '') return 'Empty';
    return String(value);
  };

  const renderChangeDetails = (entry) => {
    const { changeType, field, previousValues, newValues } = entry;

    if (changeType === 'CREATE') {
      return (
        <div className="change-details">
          <span className="change-action">created this task</span>
          {newValues && Object.keys(newValues).length > 0 && (
            <div className="initial-values">
              {Object.entries(newValues).map(([key, value]) => (
                <div key={key} className="field-value">
                  <span className="field-name">{key}:</span>
                  <span className="field-new-value">
                    {formatChangeValue(value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    const previousValue = previousValues?.[field];
    const newValue = newValues?.[field];

    return (
      <div className="change-details">
        <span className="change-action">
          changed <strong>{field}</strong>
        </span>
        <div className="value-change">
          <span className="from-value">
            from{' '}
            <span className="old-value">
              {formatChangeValue(previousValue)}
            </span>
          </span>
          <span className="to-value">
            to <span className="new-value">{formatChangeValue(newValue)}</span>
          </span>
        </div>
      </div>
    );
  };

  const filteredHistory = history.filter((entry) => {
    if (filter === 'all') return true;
    if (filter === 'changes') return entry.changeType !== 'COMMENT_ADDED';
    if (filter === 'comments') return entry.changeType === 'COMMENT_ADDED';
    return true;
  });

  if (!isOpen) return null;

  return (
    <div className="task-history-overlay">
      <div className="task-history-modal">
        <div className="task-history-header">
          <h2 className="task-history-title">
            <span className="history-icon">📋</span>
            Task Activity
          </h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* <div className="task-history-filters">
          <button 
            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Activity
          </button>
          <button 
            className={`filter-button ${filter === 'changes' ? 'active' : ''}`}
            onClick={() => setFilter('changes')}
          >
            Changes
          </button>
          <button 
            className={`filter-button ${filter === 'comments' ? 'active' : ''}`}
            onClick={() => setFilter('comments')}
          >
            Comments
          </button>
        </div> */}

        <div className="task-history-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <span>Loading activity...</span>
            </div>
          )}

          {error && (
            <div className="error-state">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
              <button className="retry-button" onClick={fetchTaskHistory}>
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && filteredHistory.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">📝</span>
              <h3>No activity yet</h3>
              <p>Activity will appear here as changes are made to this task.</p>
            </div>
          )}

          {!loading && !error && filteredHistory.length > 0 && (
            <div className="history-timeline">
              {filteredHistory.map((entry, index) => (
                <div key={entry.id || index} className="history-entry">
                  {/* <div className="entry-timeline">
                    <div 
                      className="timeline-icon"
                      style={{ backgroundColor: getChangeColor(entry.changeType) }}
                    >
                      {getChangeIcon(entry.changeType)}
                    </div>
                    {index < filteredHistory.length - 1 && <div className="timeline-line"></div>}
                  </div> */}

                  <div className="entry-content">
                    <div className="entry-header">
                      <div className="entry-user">
                        <div className="user-avatar">
                          {entry.changedBy?.userName?.charAt(0) || 'U'}
                        </div>
                        <span className="user-name">
                          {entry.changedBy?.userName || 'Unknown User'}
                        </span>
                      </div>
                      <div className="entry-timestamp">
                        {formatTimestamp(entry.timestamp)}
                      </div>
                    </div>

                    <div className="entry-description">
                      {renderChangeDetails(entry)}
                    </div>

                    {entry.commentText && (
                      <div className="comment-text">{entry.commentText}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="task-history-footer">
          <div className="history-stats">
            <span className="history-count">
              {showAllHistory
                ? `Showing all ${filteredHistory.length} ${filteredHistory.length === 1 ? 'activity' : 'activities'}`
                : `Showing ${filteredHistory.length} of ${totalCount} ${totalCount === 1 ? 'activity' : 'activities'}`}
            </span>
            <div className="footer-buttons">
              {hasMore && !showAllHistory && (
                <button
                  className="load-more-button"
                  onClick={loadMoreHistory}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              )}
              {hasOlderChunks && !showAllHistory && (
                <button
                  className="show-all-button"
                  onClick={loadCompleteHistory}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      Loading...
                    </>
                  ) : (
                    'Show All History'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskHistory;
